import api from '../../services/api';
import { events } from '../../utils/constants';

const E = events.SIGNALR;

export const DASHBOARD_SOURCE = {
	ENQUIRIES: 'enquiries',
	COMMUNICATIONS: 'communications',
	TOURS: 'tours',
	CENTRE_STATUS: 'centreStatus',
};

// One entry per API endpoint (grouped server-side by data source). `events` are the SignalR
// messages that can change that endpoint's counts; only the affected endpoint is refetched.
// Keep in sync with the API's DashboardCacheGenerations event sets.
export const DASHBOARD_SOURCES = {
	[DASHBOARD_SOURCE.ENQUIRIES]: {
		fetch: api.getDashboardEnquiryMetrics,
		events: [
			E.ENQUIRY_CREATED,
			E.ENQUIRY_UPDATED,
			E.ENQUIRY_DELETED,
			E.ENQUIRY_DUPLICATE_REMOVED,
			E.CENTRE_CREATED,
			E.CENTRE_DELETED,
			E.CENTRE_STATUS_UPDATED,
		],
	},
	[DASHBOARD_SOURCE.COMMUNICATIONS]: {
		fetch: api.getDashboardCommunicationMetrics,
		events: [
			E.COMMUNICATION_CREATED,
			E.COMMUNICATION_UPDATED,
			E.COMMUNICATION_DELETED,
			E.COMMUNICATION_STATUS_UPDATED,
			E.COMMUNICATION_INBOUND_RECEIVED,
			E.COMMUNICATION_INBOUND_UPDATED,
			E.ENQUIRY_UNREAD_COUNT_UPDATED,
			E.ENQUIRY_UPDATED,
			E.ENQUIRY_DELETED,
		],
	},
	[DASHBOARD_SOURCE.TOURS]: {
		fetch: api.getDashboardTourMetrics,
		events: [
			E.TOUR_BOOKED,
			E.TOUR_UPDATED,
			E.TOUR_CANCELLED,
			E.TOUR_COMPLETED,
			E.CENTRE_DELETED,
			E.ENQUIRY_UPDATED,
			E.ENQUIRY_DELETED,
		],
	},
	[DASHBOARD_SOURCE.CENTRE_STATUS]: {
		fetch: api.getDashboardCentreStatus,
		// Centre waitlist/tour status saves beacon the full centre; patched in place, not refetched.
		events: [],
	},
};

export const DASHBOARD_SOURCE_KEYS = Object.keys(DASHBOARD_SOURCES);

const isCancelled = (response) => response?.code === 'ERR_CANCELED';

// Returns the endpoint payload, or `undefined` when the request was cancelled (caller keeps
// its current state). Throws on failure so the caller can show the section's error state.
export async function fetchDashboardSource(key) {
	const response = await DASHBOARD_SOURCES[key].fetch();
	if (isCancelled(response)) return undefined;
	return response?.data ?? response;
}

// Centre ids referenced by a SignalR payload (enquiry summaries carry `centres`, tours/centres
// carry `centreId`). Empty when the payload has no centre information.
export function getMessageCentreIds(message) {
	if (!message || typeof message !== 'object') return [];
	const ids = (message.centres ?? []).map((centre) => centre?.centreId);
	ids.push(message.centreId);
	return ids.filter(Boolean);
}

// Centre-scoped users only refetch for messages about one of their centres. Messages without
// centre information (e.g. communications) always refetch; the server applies the real scoping.
export function isMessageInScope(message, { hasAllCentreAccess, isCentreAuthorised }) {
	if (hasAllCentreAccess) return true;
	const ids = getMessageCentreIds(message);
	return ids.length === 0 || ids.some((id) => isCentreAuthorised(id));
}

const WAITLIST_STATUS_FIELDS = [
	'waitlistTourStatusU2',
	'waitlistTourStatusNotesU2',
	'waitlistTourStatusO2',
	'waitlistTourStatusNotesO2',
	'waitlistTourLastModifiedAt',
];

// Applies a centre waitlist/tour status beacon (full centre payload) to the widget data. Returns
// the original object when the message is not a status beacon for a displayed centre.
export function mergeCentreStatusMessage(current, message) {
	if (!current?.visible || !message?.centreId) return current;
	if (!WAITLIST_STATUS_FIELDS.some((field) => field in message)) return current;

	let changed = false;
	const centres = current.centres.map((centre) => {
		if (String(centre.centreId).toLowerCase() !== String(message.centreId).toLowerCase()) return centre;
		const incomingAt = message.waitlistTourLastModifiedAt ?? 0;
		if (incomingAt && centre.waitlistTourLastModifiedAt && Number(new Date(incomingAt)) < Number(new Date(centre.waitlistTourLastModifiedAt))) return centre;
		changed = true;
		return WAITLIST_STATUS_FIELDS.reduce(
			(next, field) => (field in message ? { ...next, [field]: message[field] } : next),
			centre
		);
	});

	return changed ? { ...current, centres } : current;
}
